/**
 * CetusEd Admin Console — SSO Configuration per District
 *
 * COMPLIANCE: FERPA §1232g(b)(4)(A) — Authentication controls for systems
 *   containing PII must meet NIST SP 800-63B standards. SSO configuration
 *   includes OIDC issuer URL, client ID, and certificates for SAML.
 *
 * MIGRATION TARGET: Monolith S10 auth handles staff OIDC but has no
 *   admin-level SSO configuration UI. This provides per-district SSO
 *   management for the vendor-hosted admin console.
 */

import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSSOConfig, useUpdateSSOConfig } from '../../api/hooks';

export const SSOConfig: React.FC = () => {
  const [districtId, setDistrictId] = useState('');
  const { data: config, isLoading, error } = useSSOConfig(districtId);
  const updateConfig = useUpdateSSOConfig();

  const [editMode, setEditMode] = useState(false);
  const [provider, setProvider] = useState('');
  const [issuer, setIssuer] = useState('');
  const [clientId, setClientId] = useState('');

  const handleDistrictSelect = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setDistrictId(id);
    setEditMode(false);
  }, []);

  const handleEdit = useCallback(() => {
    if (config) {
      setProvider(config.provider || '');
      setIssuer(config.issuer || '');
      setClientId(config.clientId || '');
    }
    setEditMode(true);
  }, [config]);

  const handleSave = useCallback(() => {
    if (!districtId) return;
    // mutate() routes failures into updateConfig.isError, which is rendered
    // below — no swallowed rejection, no invisible failure.
    updateConfig.mutate(
      {
        districtId,
        config: { provider, issuer, clientId, enabled: true },
      },
      { onSuccess: () => setEditMode(false) },
    );
  }, [districtId, provider, issuer, clientId, updateConfig]);

  if (isLoading) return <div className="admin-loading">Loading SSO config...</div>;

  return (
    <div className="admin-page">
      <nav className="breadcrumb">
        <Link to="/integrations">Integrations</Link> / SSO Configuration
      </nav>

      <div className="page-header">
        <h1>SSO Configuration</h1>
      </div>

      <div className="form-field">
        <label htmlFor="sso-district">Select District</label>
        <select
          id="sso-district"
          value={districtId}
          onChange={handleDistrictSelect}
        >
          <option value="">Select a district...</option>
        </select>
        <p className="field-hint">District list loaded from the districts endpoint.</p>
      </div>

      {districtId && config && (
        <div className="detail-card">
          <h3>Current Configuration</h3>
          {editMode ? (
            <div className="admin-form">
              <div className="form-field">
                <label htmlFor="sso-provider">Provider</label>
                <select id="sso-provider" value={provider} onChange={(e) => setProvider(e.target.value)}>
                  <option value="oidc">OIDC</option>
                  <option value="saml">SAML 2.0</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor="sso-issuer">Issuer URL</label>
                <input id="sso-issuer" type="url" value={issuer} onChange={(e) => setIssuer(e.target.value)} />
              </div>
              <div className="form-field">
                <label htmlFor="sso-client-id">Client ID</label>
                <input id="sso-client-id" type="text" value={clientId} onChange={(e) => setClientId(e.target.value)} />
              </div>
              {updateConfig.isError && (
                <div className="admin-error" role="alert">
                  Failed to save SSO configuration. Please try again.
                </div>
              )}
              <div className="form-actions">
                <button className="admin-btn admin-btn--primary" onClick={handleSave} disabled={updateConfig.isPending}>
                  Save Configuration
                </button>
                <button className="admin-btn admin-btn--ghost" onClick={() => setEditMode(false)}>Cancel</button>
              </div>
            </div>
          ) : (
            <>
              <dl>
                <dt>Provider</dt>
                <dd>{config.provider || 'Not configured'}</dd>
                <dt>Status</dt>
                <dd><span className={`status-badge status-badge--${config.enabled ? 'active' : 'deactivated'}`}>
                  {config.enabled ? 'Enabled' : 'Disabled'}
                </span></dd>
                <dt>Issuer</dt>
                <dd>{config.issuer || '—'}</dd>
                {config.lastVerified && (
                  <>
                    <dt>Last Verified</dt>
                    <dd>{new Date(config.lastVerified).toLocaleString()}</dd>
                  </>
                )}
              </dl>
              <button className="admin-btn admin-btn--secondary" onClick={handleEdit}>
                Edit Configuration
              </button>
            </>
          )}
        </div>
      )}

      {districtId && error && (
        <div className="admin-error" role="alert">
          Unable to load SSO configuration for this district.
        </div>
      )}
    </div>
  );
};
